import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertsBarContainer, AlertsContext, DistaAutocomplete, DistaDialog, DistaDialogHeader, } from "@sista/dista-ui";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Right, SmartButton, useSafeSistaAsync, } from "@sista/library-stasi";
import { Box, Stack } from "@mui/system";
import { Button, DialogContent, Typography } from "@mui/material";
import { SelectNamespace } from "../parts/SelectNamespace";
export const UploadFileDialog = ({ maxFileSize, onSuccess, onClose, supportedLanguages, }) => {
    const { t } = useTranslation("sprava-textu");
    const { showSuccess, showInfo, showError } = useContext(AlertsContext);
    const [namespace, setNamespace] = useState(null);
    const [lang, setLang] = useState("cs");
    const [namespacesChanged, setNamespacesChanged] = useState(false);
    const langOptions = useMemo(() => {
        if (supportedLanguages == null || supportedLanguages.length === 0) {
            return [
                { id: "cs", label: "Čeština" },
                { id: "en", label: "English" },
            ];
        }
        return supportedLanguages.map((lang) => ({
            id: lang,
            label: lang === "cs"
                ? "Čeština"
                : lang === "en"
                    ? "English"
                    : lang === "de"
                        ? "Deutsch"
                        : lang === "fr"
                            ? "Français"
                            : lang === "es"
                                ? "Español"
                                : lang === "it"
                                    ? "Italiano"
                                    : lang === "pt"
                                        ? "Português"
                                        : lang === "ru"
                                            ? "Русский"
                                            : lang === "zh"
                                                ? "中文"
                                                : lang,
        }));
    }, [supportedLanguages]);
    const maxFileSizeVal = useMemo(() => {
        if (maxFileSize == null)
            return 5000000;
        return maxFileSize;
    }, [maxFileSize]);
    const id = "upload-file-dialog-file-input";
    const [files, setFiles] = useState(null);
    const uploadFileHandler = useCallback(async () => {
        const file = files[0];
        if (file.size > maxFileSizeVal) {
            showError(t("sprava-textu.upload.fileSizeErrorPart1", "Soubor má větší velikost než ") +
                maxFileSizeVal +
                t("sprava-textu.upload.fileSizeErrorPart2", " B."));
            return;
        }
        const formData = new FormData();
        formData.append("file", file);
        console.log("Uploading file", file.name, "to namespace", namespace, "lang", lang, "formData", formData);
        const response = await fetch(`/sprava-textu/api/v1/namespaces/${namespace.namespace}/${lang}/upload`, {
            method: "POST",
            body: formData,
            headers: {
                "X-Sprava-Textu-Token": namespace.importToken,
            },
        });
        if (response.ok) {
            const result = await response.json();
            showSuccess(t("sprava-textu.upload.fileSuccess", "Soubor úspěšně nahrán."));
            if (onSuccess) {
                onSuccess({ namespace: namespace.namespace, importStats: result });
            }
        }
        else {
            showError(t("sprava-textu.upload.fileError", "Nahrání souboru selhalo. Zkuste to znovu."));
        }
    }, [
        files,
        namespace,
        lang,
        showError,
        t,
        maxFileSizeVal,
        showSuccess,
        onSuccess,
    ]);
    const [uploadFileCall, uploadFileState] = useSafeSistaAsync(uploadFileHandler);
    const formatToFileTypeConversion = useCallback(() => {
        if (namespace == null)
            return ".json";
        switch (namespace.format) {
            case "JSON":
                return ".json";
            case "PROPERTIES":
                return ".properties";
        }
    }, [namespace]);
    return (_jsxs(DistaDialog, { open: true, onClose: onClose, children: [_jsx(DistaDialogHeader, { title: t("sprava-textu.admin.uploadFile.title", "Nahrát soubor"), onClose: onClose }), _jsxs(DialogContent, { children: [_jsxs(Stack, { direction: "column", spacing: 2, sx: { my: 2 }, children: [_jsx(SelectNamespace, { isChanged: namespacesChanged, multiple: false, onNamespacesSelected: (namespaces) => {
                                    return setNamespace(Array.isArray(namespaces) ? namespaces[0] : namespaces);
                                }, onRefresh: () => {
                                    return setNamespacesChanged(false);
                                }, helperText: namespace == null || namespace?.namespace?.trim() === ""
                                    ? t("sprava-textu.uploadFileDialog.namespaceRequired", "Tato položka je povinná.")
                                    : null, sxProps: { width: "20em" } }), _jsx(DistaAutocomplete, { label: t("sprava-textu.uploadFileDialog.label.language", "Jazyk"), options: langOptions.map((opt) => opt.id), getOptionLabel: (option) => {
                                    const lang = langOptions.find((opt) => opt.id === option);
                                    return lang ? lang.label : option;
                                }, value: lang, multiple: false, onChange: (e, newValue) => setLang(newValue), helperText: lang == null || lang.trim() === "" ? (_jsx(Typography, { color: "error", children: t("sprava-textu.uploadFileDialog.languageRequired", "Tato položka je povinná.") })) : null, size: "medium", isOptionEqualToValue: (option, value) => option === value, sx: { width: "20em" } }), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", children: [_jsxs("label", { htmlFor: id, children: [_jsx(Right, { children: _jsx(Button, { variant: "outlined", component: "span", children: t("sprava-textu.uploadFileDialog.uploadFile", "Nahrát soubor") }) }), _jsx("input", { id: id, hidden: true, accept: formatToFileTypeConversion(), type: "file", onChange: (e) => {
                                                    setFiles(e.target.files);
                                                }, multiple: false })] }), files && (_jsx(Box, { sx: { ml: 2 }, children: Array.from(files)
                                            .map((file) => file.name)
                                            .join(", ") }))] }), files == null && files?.length === 0 && (_jsx(Typography, { color: "error", children: t("sprava-textu.uploadFileDialog.noFileSelected", "Žádný soubor nebyl vybrán.") })), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(SmartButton, { variant: "contained", onClick: () => {
                                            uploadFileCall({});
                                        }, state: uploadFileState, disabled: uploadFileState.pending ||
                                            namespace == null ||
                                            namespace?.namespace?.trim() === "" ||
                                            lang == null ||
                                            lang.trim() === "" ||
                                            files == null ||
                                            files.length === 0, children: t("sprava-textu.uploadFileDialog.upload", "Nahrát") })] })] }), _jsx(AlertsBarContainer, { id: "alerts-in-dialog" })] })] }));
};
